import {
  HSEQ_STAGES,
  type HseqStage,
  type HseReportType,
  type HseStatus,
  hseqStageOf,
} from '@felix/domain';
import { Button } from '@mantine/core';
import {
  IconAlertTriangleFilled,
  IconCheck,
  IconCircleCheckFilled,
  IconCircleXFilled,
  IconFileTypePdf,
  IconFirstAidKit,
  IconPhoto,
  IconRun,
} from '@tabler/icons-react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { AttachmentFileActions } from '../../attachments/AttachmentFileActions.js';
import { EmptyState } from '../../EmptyState.js';
import { formatDateTime } from '../../format.js';
import { StatusBadge } from '../../StatusBadge.js';
import { fileSize } from '../pms/AttachmentCard.js';
import classes from './Hse.module.css';
import { hsePriorityTone, hseqStageTone, hseStatusTone } from './routes.js';
import type { HseDetail, HseFile } from './types.js';

/** Hata kodunun metni: bilinen kod `hse.errors.*`, alan ayrıntısı varsa eklenir. */
export function hseErrorText(t: TFunction, error: unknown): string {
  const code = (error as { code?: unknown } | null)?.code;
  const detail = (error as { detail?: unknown } | null)?.detail;
  const key = typeof code === 'string' ? code : 'GENERIC';
  const text = t(`hse.errors.${key}`, { defaultValue: t('hse.errors.GENERIC', { code: key }) });
  if (key === 'HSE_NOT_CLOSABLE' && typeof detail === 'string') {
    return `${text} ${detail
      .split(',')
      .map((gap) => t(`hse.closure.gaps.${gap}`, { defaultValue: gap }))
      .join('; ')}`;
  }
  return text;
}

export function HseStatusBadge({ status }: { status: HseStatus }) {
  const { t } = useTranslation();
  return <StatusBadge tone={hseStatusTone(status)}>{t(`hse.statuses.${status}`)}</StatusBadge>;
}

export function HsePriorityBadge({ priority }: { priority: string | null }) {
  const { t } = useTranslation();
  if (!priority) return <span className={classes.muted}>—</span>;
  return (
    <StatusBadge tone={hsePriorityTone(priority)}>{t(`hse.priorities.${priority}`)}</StatusBadge>
  );
}

const ACTION_TONE: Record<string, 'success' | 'warning' | 'info' | 'neutral' | 'danger'> = {
  planned: 'warning',
  in_progress: 'info',
  completed: 'success',
  cancelled: 'neutral',
  open: 'danger',
  closed: 'success',
};

/** Faaliyet ve bulgu durumu (BGM: Planned turuncu, In Progress mavi, Completed yeşil). */
export function HseStepBadge({ status, kind }: { status: string; kind: 'action' | 'finding' }) {
  const { t } = useTranslation();
  return (
    <StatusBadge tone={ACTION_TONE[status] ?? 'neutral'}>
      {t(kind === 'action' ? `hse.actionStatuses.${status}` : `hse.findingStatuses.${status}`)}
    </StatusBadge>
  );
}

const TYPE_ICON: Record<HseReportType, typeof IconAlertTriangleFilled> = {
  non_conformity: IconAlertTriangleFilled,
  deficiency: IconAlertTriangleFilled,
  near_miss: IconRun,
  accident: IconFirstAidKit,
};

export function HseTypeIcon({ type }: { type: HseReportType }) {
  const Icon = TYPE_ICON[type];
  return (
    <span className={classes.typeIcon} data-type={type} aria-hidden>
      <Icon size={20} />
    </span>
  );
}

const FLOW: readonly HseStatus[] = [
  'draft',
  'submitted',
  'under_review',
  'action_assigned',
  'in_progress',
  'pending_verification',
  'closed',
];

/** İş akışı (BGM "Non Conformity İş Akışı"; ortak §5): durumlar sırayla, bulunulan vurgulu. */
export function HseStepper({ status }: { status: HseStatus }) {
  const { t } = useTranslation();
  const current = status === 'reopened' ? FLOW.indexOf('in_progress') : FLOW.indexOf(status);
  return (
    <ol className={classes.stepper} aria-label={t('hse.record.workflow')}>
      {FLOW.map((step, index) => {
        const state = index < current ? 'done' : index === current ? 'current' : 'next';
        return (
          <li
            key={step}
            className={classes.step}
            data-state={state}
            aria-current={state === 'current' ? 'step' : undefined}
          >
            <span className={classes.stepNo}>{index + 1}</span>
            <span>
              {t(`hse.statuses.${step}`)}
              {state === 'current' && status === 'reopened'
                ? ` (${t('hse.statuses.reopened')})`
                : ''}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

/**
 * Ek: ad, boyut, zaman ve ofisteki durumu; doğrulanmış dosya ofiste açılır. "Göz" ve indirme
 * simgeleri gemide de (ADR 0126): adres verilmezse uygulamanın ek erişiminden.
 */
export function HseFileItem({ file, href }: { file: HseFile; href?: string | undefined }) {
  const { t, i18n } = useTranslation();
  const pdf = file.mimeType === 'application/pdf';
  const available =
    !file.voided && (file.officeState === undefined || file.officeState === 'verified');
  const openable = href !== undefined && available;
  return (
    <li className={classes.file} data-voided={file.voided || undefined}>
      <span className={classes.linkIcon} aria-hidden>
        {pdf ? <IconFileTypePdf size={22} /> : <IconPhoto size={22} />}
      </span>
      {openable ? (
        <a className={classes.link} href={href} target="_blank" rel="noopener">
          {file.fileName}
        </a>
      ) : (
        <span className={classes.fileName}>{file.fileName}</span>
      )}
      <span>
        {fileSize(file.size, i18n.resolvedLanguage ?? 'en')} · {formatDateTime(file.createdAt)}
      </span>
      {file.voided ? (
        <span>{t('hse.files.voided')}</span>
      ) : file.officeState && file.officeState !== 'verified' ? (
        <span>{t(`hse.files.states.${file.officeState}`, { defaultValue: file.officeState })}</span>
      ) : null}
      {available ? (
        <AttachmentFileActions
          id={file.id}
          fileName={file.fileName}
          mimeType={file.mimeType}
          href={href}
        />
      ) : null}
    </li>
  );
}

export function CheckMark({ ok }: { ok: boolean }) {
  return ok ? (
    <IconCircleCheckFilled size={16} aria-hidden />
  ) : (
    <IconCircleXFilled size={16} aria-hidden />
  );
}

export function HseLoadError({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  const { t } = useTranslation();
  return (
    <EmptyState
      tone="danger"
      title={t('hse.list.error')}
      description={hseErrorText(t, error)}
      action={
        <Button size="xs" variant="light" onClick={onRetry}>
          {t('hse.list.retry')}
        </Button>
      }
    />
  );
}

export interface HseqStageStep {
  stage: HseqStage;
  state: 'done' | 'current' | 'next';
  at: string | null;
  by: string | null;
}

/**
 * Dört aşamanın durumu ve zamanı (ADR 0097 §3): Reported gönderim, Under Action kaptan incelemesi,
 * Verification son kapanış gönderimi, Closed onay kararı. Kapanmış raporda hepsi tamam.
 */
export function hseqSteps(detail: HseDetail | null, status: HseStatus): HseqStageStep[] {
  const current = HSEQ_STAGES.indexOf(hseqStageOf(status));
  const closed = status === 'closed';
  const lastSubmission = detail?.approval.submissions.at(-1) ?? null;
  const approved =
    [...(detail?.approval.decisions ?? [])].reverse().find((d) => d.decision === 'approved') ??
    null;
  const marks: Record<HseqStage, { at: string | null; by: string | null }> = {
    reported: {
      at: detail?.submittedAt ?? detail?.createdAt ?? null,
      by: detail?.createdBy ?? null,
    },
    under_action: { at: detail?.report.reviewedAt ?? null, by: detail?.report.reviewedBy ?? null },
    verification: {
      at: current >= 2 ? (lastSubmission?.at ?? null) : null,
      by: current >= 2 ? (lastSubmission?.by ?? null) : null,
    },
    closed: {
      at: closed ? (approved?.at ?? null) : null,
      by: closed ? (approved?.by ?? null) : null,
    },
  };
  return HSEQ_STAGES.map((stage, index) => ({
    stage,
    state: closed || index < current ? 'done' : index === current ? 'current' : 'next',
    ...marks[stage],
  }));
}

/** Dört aşamalı çizgi (hseq-ekran.jpeg üst bölüm): numara, ad, alt ad ve zaman. */
export function HseqStages({ steps }: { steps: readonly HseqStageStep[] }) {
  const { t } = useTranslation();
  return (
    <ol className={classes.stages} aria-label={t('hse.hseq.stepper')}>
      {steps.map((step, index) => (
        <li
          key={step.stage}
          className={classes.stage}
          data-state={step.state}
          aria-current={step.state === 'current' ? 'step' : undefined}
        >
          <span className={classes.stageDot} aria-hidden>
            {step.state === 'done' ? <IconCheck size={18} /> : index + 1}
          </span>
          <span className={classes.stageName}>{t(`hse.hseq.stages.${step.stage}`)}</span>
          <span>{t(`hse.hseq.stageNotes.${step.stage}`)}</span>
          <span>{step.at ? formatDateTime(step.at) : '—'}</span>
        </li>
      ))}
    </ol>
  );
}

/** Aşama rozeti (liste): dört aşamadan biri. */
export function HseqStageBadge({ status }: { status: HseStatus }) {
  const { t } = useTranslation();
  const stage = hseqStageOf(status);
  return <StatusBadge tone={hseqStageTone(stage)}>{t(`hse.hseq.stages.${stage}`)}</StatusBadge>;
}
